import { Clipboard } from '@angular/cdk/clipboard';
import { NgClass } from '@angular/common';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { JobRequirementsClient } from 'src/app/compliance/api/job-requirements-client.service';
import { JobsRequirementsImportResultDto } from 'src/app/compliance/api/models/jobs-requirements-import-result-dto.model';
import { Owner } from 'src/app/shared/helpers/owner.decorator';

@Owner("AO Let's Go")
@Component({
    selector: 'nova-import-job-requirements',
    templateUrl: './import-job-requirements.component.html',
    styleUrls: ['./import-job-requirements.component.scss'],
    standalone: true,
    imports: [MatIconModule, MatProgressSpinnerModule, MatButtonModule, NgClass]
})
export class ImportJobRequirementsComponent {
    @ViewChild('fileInput', { static: true }) fileInput: ElementRef;

    private readonly maxFileSize = 50 * 1000000;
    public readonly facilityProfileId: number;

    isDragging = false;
    isSaving = false;
    file: File;

    public result: Partial<JobsRequirementsImportResultDto> = {
        imported: false,
        isValid: false,
        preExistingFound: false,
        invalidJobs: [],
        missingJobs: [],
        validJobs: [],
        duplicateJobs: []
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: { facilityProfileId: number },
        private readonly _dialogRef: MatDialogRef<ImportJobRequirementsComponent>,
        private readonly _toasterService: ToasterService,
        private readonly _clipboard: Clipboard,
        private readonly _confirmationService: ConfirmDialogService,
        private readonly jobRequirementsClient: JobRequirementsClient
    ) {
        this.facilityProfileId = this.dialogData.facilityProfileId;
    }

    addSelectedFiles(): void {
        this.addFiles(this.fileInput.nativeElement.files);
        this.fileInput.nativeElement.value = null;
    }

    updateDragState($event: DragEvent, isDragging: boolean): void {
        $event.stopPropagation();
        $event.preventDefault();

        this.isDragging = isDragging;
    }

    getDroppedFiles($event: DragEvent): void {
        $event.preventDefault();

        this.isDragging = false;
        this.addFiles($event?.dataTransfer?.files);
    }

    browse($event: MouseEvent): void {
        $event.preventDefault();
        this.fileInput.nativeElement.click();
    }

    importRequirements(): void {
        if (this.result.isValid) {
            if (this.result.preExistingFound) {
                this._confirmationService
                    .showDialog({
                        title: 'Pre-Existing requirements found',
                        message: `Previously imported requirements will be replaced`,
                        action: 'IMPORT ANYWAY',
                        cancelText: 'CANCEL',
                        disableClose: true
                    })
                    .subscribe((confirm) => {
                        if (confirm) {
                            this.importConfirmed();
                        }
                    });
            } else {
                this.importConfirmed();
            }
        }
    }

    importConfirmed(): void {
        this.isSaving = true;
        this.jobRequirementsClient
            .import(this.facilityProfileId, false, { data: this.file, fileName: this.file.name })
            .subscribe({
                next: (result) => {
                    this.result = result;
                    this.file = null;
                },
                error: (_) => {
                    this.isSaving = false;
                    this._toasterService.toast('error verifying file');
                },
                complete: () => (this.isSaving = false)
            });
    }

    cancel(): void {
        this._dialogRef.close();
    }

    validateFile(file: File): boolean {
        return file.size < this.maxFileSize;
    }

    addFiles(value: FileList): void {
        this.isSaving = true;
        this.result.isValid = false;
        this.file = value[0];
        if (this.validateFile(this.file)) {
            this.jobRequirementsClient
                .import(this.facilityProfileId, true, { data: this.file, fileName: this.file.name })
                .subscribe({
                    next: (result) => {
                        this.result = result;
                    },
                    error: (err) => this._toasterService.toast(err),
                    complete: () => (this.isSaving = false)
                });
        }
    }

    copyInvalidIds(): void {
        const txt = this.result.invalidJobs.join(', ');
        this._clipboard.copy(txt);
    }

    copyDuplicateIds(): void {
        const txt = this.result.duplicateJobs.join(', ');
        this._clipboard.copy(txt);
    }

    copyValidIds(): void {
        const txt = this.result.validJobs.join(', ');
        this._clipboard.copy(txt);
    }

    copyMissingIds(): void {
        const txt = this.result.missingJobs.join(', ');
        this._clipboard.copy(txt);
    }
}
